'use strict';

(function initializeOneShotRecentAdapter(root) {
    const app = root.dndNextLevel;
    const TYPE = 'one-shot';
    const RESTORE_KEY = 'dnd-next-level:one-shot:restore:v1';
    const STAGES = ['OPENING', 'WILDERNESS', 'SETTLEMENT', 'DUNGEON', 'FINALE'];
    const clone = value => JSON.parse(JSON.stringify(value));
    const hasText = (value, max = 4000) =>
        typeof value === 'string' && value.trim().length > 0 && value.length <= max;
    const normalizedText = value => String(value || '').replace(/\s+/g, ' ').trim();

    const validStage = (stage, index) => Boolean(
        stage && stage.type === STAGES[index] &&
        hasText(stage.title, 80) && hasText(stage.summary, 600) &&
        Array.isArray(stage.outline) && stage.outline.length > 0 && stage.outline.length <= 40 &&
        stage.outline.every(line => hasText(line, 2000))
    );

    const validSnapshot = snapshot => {
        if (!snapshot || snapshot.version !== 1 || !hasText(snapshot.tier, 100) ||
            typeof snapshot.completed !== 'boolean' || !Array.isArray(snapshot.stages) ||
            snapshot.stages.length < 1 || snapshot.stages.length > STAGES.length ||
            !snapshot.stages.every(validStage)) {
            return false;
        }
        if (snapshot.completed) {
            return snapshot.currentStage === 'COMPLETE' && snapshot.stages.length === STAGES.length;
        }
        return snapshot.currentStage === snapshot.stages.at(-1).type;
    };

    const adapter = {
        type: TYPE,
        route: '/one-shot',
        capture(source) {
            const snapshot = source?.snapshot || source;
            return validSnapshot(snapshot) ? clone(snapshot) : null;
        },
        validate: validSnapshot,
        preview(payload) {
            if (!validSnapshot(payload)) throw new Error('Invalid One-Shot recent creation');
            const stage = payload.completed ? 'Complete' : payload.stages.at(-1).title;
            return {
                title: `One Shot · ${payload.tier}`,
                description: `${payload.stages.length} of 5 stages · ${stage}`
            };
        },
        restore(payload) {
            if (!validSnapshot(payload)) throw new Error('Invalid One-Shot recent creation');
            root.sessionStorage.setItem(RESTORE_KEY, JSON.stringify(payload));
            root.location.assign(`${adapter.route}?restore=recent#recent-snapshot`);
            return clone(payload);
        }
    };

    const captureDocument = document => {
        const workspace = document.querySelector?.('#one-shot-workspace');
        if (!workspace?.dataset?.tier || !workspace.dataset.currentStage) return null;
        const stageElements = Array.from(
            document.querySelectorAll?.('[data-component="one-shot-stage-disclosure"]') || []
        );
        const stages = stageElements.map(stageElement => {
            const type = stageElement.dataset.stage;
            const contentNodes = Array.from(stageElement.querySelectorAll?.(
                '.one-shot-beat__content, .one-shot-beat__supporting-content, .one-shot-dungeon-room p'
            ) || []);
            return {
                type,
                title: STAGES.includes(type)
                    ? type.charAt(0) + type.slice(1).toLowerCase()
                    : '',
                summary: normalizedText(
                    stageElement.querySelector?.('[data-slot="stage-summary"]')?.textContent
                ),
                outline: contentNodes.map(node => normalizedText(node.textContent)).filter(Boolean)
            };
        });
        return adapter.capture({
            version: 1,
            tier: workspace.dataset.tier,
            currentStage: workspace.dataset.currentStage,
            completed: workspace.dataset.completed === 'true',
            stages
        });
    };

    const formatOutline = payload => {
        if (!validSnapshot(payload)) throw new Error('Invalid One-Shot recent creation');
        const lines = [
            'D&D One-Shot',
            `Tier: ${payload.tier}`,
            `Progress: ${payload.completed ? 'Complete' : payload.stages.at(-1).title}`
        ];
        payload.stages.forEach(stage => {
            lines.push('', stage.type, stage.summary, ...stage.outline.map(line => `- ${line}`));
        });
        return lines.join('\n');
    };

    const readShared = storage => app.recentCreationStore.create(storage).read()
        .filter(entry => entry.type === TYPE);

    const saveCurrent = (source, storage = root.localStorage, pinned = false, now = Date.now()) => {
        const payload = adapter.capture(source);
        if (!payload || !app.recentCreationStore) return null;
        const store = app.recentCreationStore.create(storage);
        const previous = store.read().find(entry => entry.type === TYPE && entry.id === 'current');
        const entry = {
            id: 'current',
            type: TYPE,
            createdAt: previous?.createdAt ?? now,
            updatedAt: now,
            pinned: pinned || previous?.pinned || false,
            payload
        };
        store.save(entry);
        app.recentCreations?.instance?.render();
        return entry;
    };

    const consumeRestore = (storage = root.sessionStorage) => {
        const raw = storage.getItem(RESTORE_KEY);
        storage.removeItem(RESTORE_KEY);
        if (!raw) return null;
        try {
            const payload = JSON.parse(raw);
            return validSnapshot(payload) ? clone(payload) : null;
        } catch (_error) {
            return null;
        }
    };

    const renderReadOnly = (payload, document) => {
        if (!validSnapshot(payload)) return false;
        const snapshotRoot = document.querySelector?.('#one-shot-read-only-snapshot');
        const workspace = document.querySelector?.('#one-shot-workspace');
        const list = snapshotRoot?.querySelector?.('[data-slot="snapshot-stages"]');
        if (!snapshotRoot || !workspace || !list) return false;

        list.replaceChildren();
        payload.stages.forEach(stage => {
            const item = document.createElement('li');
            const heading = document.createElement('h3');
            const summary = document.createElement('p');
            const outline = document.createElement('ul');
            heading.textContent = stage.title;
            summary.textContent = stage.summary;
            stage.outline.forEach(line => {
                const point = document.createElement('li');
                point.textContent = line;
                outline.append(point);
            });
            heading.className = 'text-xl font-semibold';
            summary.className = 'mt-1 text-sm font-medium text-slate-600 dark:text-slate-300';
            outline.className = 'mt-3 list-disc space-y-1 pl-5 text-sm';
            item.className = 'rounded-2xl border border-slate-200 p-4 dark:border-zinc-700';
            item.append(heading, summary, outline);
            list.append(item);
        });
        snapshotRoot.querySelector('[data-slot="snapshot-tier"]').textContent = payload.tier;
        snapshotRoot.hidden = false;
        workspace.hidden = true;
        return true;
    };

    app.oneShotRecentAdapter = {
        adapter,
        captureDocument,
        formatOutline,
        saveCurrent,
        readShared,
        consumeRestore,
        renderReadOnly,
        RESTORE_KEY
    };
    app.recentCreationAdapters?.registry?.register(adapter);
})(window);
